import React from 'react';
import { MessageCircle, Smartphone, Briefcase, BookOpen, Zap, Building2, Edit3 } from 'lucide-react';

interface Step6Data {
  tone: string;
}

interface Step6ToneProps {
  data: Step6Data;
  onChange: (data: Step6Data) => void;
}

const TONE_OPTIONS = [
  {
    value: 'persoonlijk-toegankelijk',
    label: 'Persoonlijk & toegankelijk',
    description: 'Warm, vriendelijk, informeel. Alsof je een goede vriend helpt.',
    example: 'Hey! Heb je ook zo\'n hekel aan supermarktbrood? Wij ook. Daarom bakken we elke dag vers...',
    icon: Smartphone,
    recommended: true
  },
  {
    value: 'professioneel-betrouwbaar',
    label: 'Professioneel & betrouwbaar',
    description: 'Zakelijk maar menselijk. Expert met een persoonlijke touch.',
    example: 'We begrijpen dat je gezond wilt eten zonder gedoe. Daarom werken we uitsluitend met lokale...',
    icon: Briefcase
  },
  {
    value: 'helder-informatief',
    label: 'Helder & informatief',
    description: 'Leerzaam, duidelijke uitleg, stap voor stap.',
    example: 'Goed brood begint bij goede ingrediënten. Stap 1: We selecteren lokaal graan. Stap 2...',
    icon: BookOpen
  },
  {
    value: 'energiek-inspirerend',
    label: 'Energiek & inspirerend',
    description: 'Positief, motiverend, enthousiast.',
    example: 'Stel je voor: elke ochtend wakker worden met de geur van vers brood! Dat is wat wij mogelijk maken...',
    icon: Zap
  },
  {
    value: 'formeel-zakelijk',
    label: 'Formeel & zakelijk',
    description: 'Serieus, neutraal, corporate.',
    example: 'Onze bakkerij biedt ambachtelijke producten van hoge kwaliteit. Wij hanteren strikte kwaliteitsnormen...',
    icon: Building2
  },
];

export function Step6Tone({ data, onChange }: Step6ToneProps) {
  const [showCustomField, setShowCustomField] = React.useState(false);
  const [customTone, setCustomTone] = React.useState('');

  const handleSelectTone = (tone: string) => {
    onChange({ ...data, tone });
    setShowCustomField(false);
    setCustomTone('');
  };

  const handleCustomTone = (value: string) => {
    setCustomTone(value);
    onChange({ ...data, tone: value });
  };

  const toggleCustomField = () => {
    setShowCustomField(!showCustomField);
    if (!showCustomField) {
      onChange({ ...data, tone: customTone });
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
          <MessageCircle className="w-4 h-4" />
          Kies je communicatiestijl
        </label>
        <div className="space-y-3">
          {TONE_OPTIONS.map((option) => {
            const Icon = option.icon;
            return (
              <button
                key={option.value}
                onClick={() => handleSelectTone(option.value)}
                className={`w-full p-4 border-2 rounded-xl text-left transition-all ${
                  data.tone === option.value
                    ? 'border-blue-600 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex items-start gap-3">
                  <Icon className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <div className="font-medium text-gray-900 mb-1 flex items-center gap-2">
                      {option.label}
                      {option.recommended && (
                        <span className="text-xs bg-blue-600 text-white px-2 py-0.5 rounded-full">
                          Aanbevolen
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-gray-600 mb-2">{option.description}</div>
                    <div className="text-xs text-gray-500 italic bg-gray-50 rounded p-2 mt-2">
                      "{option.example}"
                    </div>
                  </div>
                </div>
              </button>
            );
          })}

          <button
            onClick={toggleCustomField}
            className={`w-full p-4 border-2 rounded-xl text-left transition-all ${
              showCustomField || (customTone && !TONE_OPTIONS.some(opt => opt.value === data.tone))
                ? 'border-blue-600 bg-blue-50'
                : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            <div className="flex items-start gap-3">
              <Edit3 className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <div className="font-medium text-gray-900 mb-1">Anders</div>
                {showCustomField && (
                  <input
                    type="text"
                    value={customTone}
                    onChange={(e) => handleCustomTone(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    className="w-full mt-2 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Gezellig en huiselijk, alsof je bij oma binnenloopt. Nostalgisch maar wel modern"
                    autoFocus
                  />
                )}
              </div>
            </div>
          </button>
        </div>
      </div>
    </div>
  );
}
